import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { WidgetForm, WidgetFormValues } from "../components/widgets/WidgetForm";
import { toast } from "sonner";
import { useClipboard } from "../utils/useClipboard";

type Widget = {
  id: string;
  name: string;
  description?: string;
  embedKey: string;
  publicUrl: string;
  status: "draft" | "active" | "archived";
  createdAt: number;
};

type WidgetsResponse = {
  widgets: Widget[];
};

type EmbedResponse = {
  widget: Widget;
  embedCode: string;
  shareLink: string;
};

export const WidgetsPage = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editState, setEditState] = useState<{ open: boolean; widget: Widget | null }>({
    open: false,
    widget: null
  });
  const [embedState, setEmbedState] = useState<{ widgetId: string | null; code: string; link: string }>({
    widgetId: null,
    code: "",
    link: ""
  });
  const [deleteTarget, setDeleteTarget] = useState<Widget | null>(null);
  const queryClient = useQueryClient();
  const { copy } = useClipboard();

  const { data, isLoading } = useQuery({
    queryKey: ["widgets"],
    queryFn: async () => {
      const response = await apiClient.get<WidgetsResponse>("/widgets");
      return response.data.widgets;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (values: WidgetFormValues) => {
      await apiClient.post("/widgets", values);
    },
    onSuccess: () => {
      toast.success("Widget created");
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["widgets"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Failed to create widget")
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: WidgetFormValues) => {
      if (!payload.id) throw new Error("Missing widget id");
      await apiClient.put(`/widgets/${payload.id}`, payload);
    },
    onSuccess: () => {
      toast.success("Widget updated");
      setEditState({ open: false, widget: null });
      queryClient.invalidateQueries({ queryKey: ["widgets"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Update failed")
  });

  const deleteMutation = useMutation({
    mutationFn: async (widgetId: string) => {
      await apiClient.delete(`/widgets/${widgetId}`);
    },
    onSuccess: () => {
      toast.success("Widget deleted");
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["widgets"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Deletion failed")
  });

  const embedMutation = useMutation({
    mutationFn: async (widgetId: string) => {
      const response = await apiClient.get<EmbedResponse>(`/widgets/${widgetId}/embed`);
      return response.data;
    },
    onSuccess: (data) => {
      setEmbedState({ widgetId: data.widget.id, code: data.embedCode, link: data.shareLink });
      toast.success("Embed code generated");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Failed to build embed snippet")
  });

  const widgets = data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-100">Widget Management</h2>
          <p className="text-sm text-slate-400">
            Launch embeddable and full-screen experiences with unique identifiers and instant embed
            snippets.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>Create Widget</Button>
      </div>

      <Card>
        <CardHeader>
          <p className="text-sm font-medium text-slate-200">Available widgets</p>
          <p className="text-xs text-slate-500">
            Each widget is tracked via Durable Objects and is ready for Cloudflare Worker deployment.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : widgets.length === 0 ? (
            <p className="text-sm text-slate-400">No widgets yet. Create one to get started.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Embed Key</TH>
                  <TH>Status</TH>
                  <TH>Public URL</TH>
                  <TH></TH>
                </TR>
              </THead>
              <TBody>
                {widgets.map((widget) => (
                  <TR key={widget.id}>
                    <TD className="font-medium text-slate-100">
                      <div className="space-y-1">
                        <p>{widget.name}</p>
                        <p className="text-xs text-slate-500">ID: {widget.id}</p>
                      </div>
                    </TD>
                    <TD>
                      <code className="rounded bg-slate-900 px-2 py-1 text-xs text-slate-300">
                        {widget.embedKey}
                      </code>
                    </TD>
                    <TD>
                      <Badge
                        variant={
                          widget.status === "active"
                            ? "success"
                            : widget.status === "archived"
                            ? "warning"
                            : "info"
                        }
                      >
                        {widget.status}
                      </Badge>
                    </TD>
                    <TD className="text-xs text-slate-400">{widget.publicUrl}</TD>
                    <TD>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => embedMutation.mutate(widget.id)}
                        >
                          Embed Code
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditState({ open: true, widget })}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-200"
                          onClick={() => setDeleteTarget(widget)}
                        >
                          Remove
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {embedState.widgetId ? (
        <Card>
          <CardHeader>
            <p className="text-sm font-medium text-slate-200">Deployment snippet</p>
            <p className="text-xs text-slate-500">
              Paste this script into the host site or share the dedicated conversation page.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase text-slate-500">Embed Code</p>
                <pre className="mt-2 overflow-x-auto rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-200">
                  {embedState.code}
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => copy(embedState.code)}
                >
                  Copy Embed Script
                </Button>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Shareable Link</p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 truncate rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-xs text-slate-200">
                    {embedState.link}
                  </code>
                  <Button variant="secondary" size="sm" onClick={() => copy(embedState.link)}>
                    Copy Link
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Modal
        title="Create Widget"
        description="Define a new widget that can be embedded or launched in full-screen mode."
        open={createOpen}
        onOpenChange={setCreateOpen}
      >
        <WidgetForm
          mode="create"
          onSubmit={(values) => createMutation.mutateAsync(values)}
          defaultValues={{ name: "", description: "", status: "draft" }}
        />
      </Modal>

      <Modal
        title="Update Widget"
        description="Refresh branding, availability, and embed configuration."
        open={editState.open}
        onOpenChange={(value) => setEditState((prev) => ({ ...prev, open: value }))}
      >
        {editState.widget ? (
          <WidgetForm
            mode="update"
            defaultValues={{
              id: editState.widget.id,
              name: editState.widget.name,
              description: editState.widget.description,
              status: editState.widget.status
            }}
            onSubmit={(values) =>
              updateMutation.mutateAsync({
                ...values,
                id: editState.widget?.id
              })
            }
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        title="Delete widget"
        description={
          deleteTarget ? (
            <span>
              Removing <strong>{deleteTarget.name}</strong> will invalidate its embed code and public
              chat link.
            </span>
          ) : (
            "This action cannot be undone."
          )
        }
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        confirmLabel="Delete widget"
        loadingLabel="Deleting..."
        destructive
        loading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteMutation.mutateAsync(deleteTarget.id);
          } catch {
            // handled via mutation onError
          }
        }}
      />
    </div>
  );
};
